import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  UsersRound, CheckCircle2, Megaphone,
  AlertTriangle, ShieldCheck, Siren, ChevronRight, Calendar, RefreshCw,
  Ticket, Gavel, MessageCircle, CheckCheck, Send, Loader2, Pin, Trash2,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { useAdminAuth } from "./AdminAuth";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const roleLabel = (r) => (r === "super_admin" ? "Super Admin" : r);

function BoardPanel() {
  const { admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [notes, setNotes] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [threads, setThreads] = useState({});   // noteId -> thread open
  const [replyText, setReplyText] = useState({}); // noteId -> draft
  const [replying, setReplying] = useState("");

  const load = useCallback(() => {
    adminApi.get("/board/notes").then((r) => setNotes(r.data.items || [])).catch(() => setNotes([]));
    if (!isSuper) {
      adminApi.get("/employees/notes").then((r) => setMyNotes(r.data.items || [])).catch(() => setMyNotes([]));
    }
  }, [isSuper]);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await adminApi.post("/board/notes", { body: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not post the note");
    } finally {
      setPosting(false);
    }
  };

  if (notes === null) return null;
  const [latest, ...rest] = notes;

  const togglePin = async (n) => {
    try {
      await adminApi.post(`/board/notes/${n.id}/pin`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not pin the note");
    }
  };

  const removeNote = async (n) => {
    try {
      await adminApi.delete(`/board/notes/${n.id}`);
      toast.success("Note deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete the note");
    }
  };

  const sendReply = async (noteId) => {
    const reply = (replyText[noteId] || "").trim();
    if (!reply || replying) return;
    setReplying(noteId);
    try {
      await adminApi.post(`/board/notes/${noteId}/replies`, { body: reply });
      setReplyText((m) => ({ ...m, [noteId]: "" }));
      setThreads((m) => ({ ...m, [noteId]: true }));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not post the reply");
    } finally {
      setReplying("");
    }
  };

  const removeReply = async (noteId, replyId) => {
    try {
      await adminApi.delete(`/board/notes/${noteId}/replies/${replyId}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete the reply");
    }
  };

  const ReplyToggle = ({ n, light }) => (
    <button data-testid={`board-reply-toggle-${n.id}`}
      onClick={() => setThreads((m) => ({ ...m, [n.id]: !m[n.id] }))}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${light ? "text-[#565b73] hover:bg-white/60" : "text-[#2340b5] hover:bg-[#2340b5]/8"}`}>
      <MessageCircle className="h-3.5 w-3.5" />
      {(n.replies || []).length > 0 ? `${n.replies.length} ${n.replies.length === 1 ? "reply" : "replies"}` : "Reply"}
    </button>
  );

  const Thread = ({ n }) => !threads[n.id] ? null : (
    <div className="mt-3 space-y-2 border-t border-[#12142b]/10 pt-3" data-testid={`board-replies-${n.id}`}>
      {(n.replies || []).map((r) => (
        <div key={r.id} data-testid={`board-reply-row-${r.id}`} className="flex items-start gap-2.5 rounded-lg bg-white/80 p-2.5">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#2340b5]/10 text-[10px] font-bold text-[#2340b5]">{initials(r.author_name || r.author_email)}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-[#12142b]">{r.body}</p>
            <p className="mt-0.5 text-xs text-[#565b73]">
              {r.author_name || r.author_email}{r.author_role ? ` · ${roleLabel(r.author_role)}` : ""} · {timeAgo(r.created_at)}
            </p>
          </div>
          {isSuper && (
            <button data-testid={`board-reply-delete-${r.id}`} onClick={() => removeReply(n.id, r.id)} title="Delete reply"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[#565b73] transition-colors hover:bg-[#e4002b]/8 hover:text-[#e4002b]">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input
          data-testid={`board-reply-input-${n.id}`} value={replyText[n.id] || ""} maxLength={280}
          onChange={(e) => setReplyText((m) => ({ ...m, [n.id]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") sendReply(n.id); }}
          placeholder="Write a reply…"
          className="flex-1 rounded-lg border border-[#12142b]/12 bg-white px-3 py-2 text-sm text-[#12142b] outline-none focus:border-[#2340b5]"
        />
        <button data-testid={`board-reply-send-${n.id}`} onClick={() => sendReply(n.id)} disabled={replying === n.id || !(replyText[n.id] || "").trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#2340b5] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#1b3390] disabled:opacity-50">
          {replying === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Reply
        </button>
      </div>
    </div>
  );

  const Controls = ({ n }) => !isSuper ? null : (
    <div className="flex shrink-0 items-center gap-1">
      <button data-testid={`board-pin-${n.id}`} onClick={() => togglePin(n)} title={n.pinned ? "Unpin" : "Pin to top"}
        className={`grid h-8 w-8 place-items-center rounded-lg transition-colors ${n.pinned ? "bg-[#f59e0b]/15 text-[#b45309]" : "text-[#565b73] hover:bg-[#12142b]/6"}`}>
        <Pin className="h-4 w-4" />
      </button>
      <button data-testid={`board-delete-${n.id}`} onClick={() => removeNote(n)} title="Delete note"
        className="grid h-8 w-8 place-items-center rounded-lg text-[#565b73] transition-colors hover:bg-[#e4002b]/8 hover:text-[#e4002b]">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  const PinnedChip = () => (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#f59e0b]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#b45309]">
      <Pin className="h-3 w-3" /> Pinned
    </span>
  );

  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid="board-panel">
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#2340b5]/8"><Megaphone className="h-5 w-5 text-[#2340b5]" /></span>
        <div>
          <h2 className="font-display text-lg font-bold text-[#12142b]">SafeRide Board</h2>
          <p className="text-xs text-[#565b73]">Shared notes for the whole team — everyone can post</p>
        </div>
      </div>

      {/* Newest note, big */}
      {latest ? (
        <div className="rounded-2xl bg-[#2340b5]/5 px-6 py-6" data-testid="board-latest">
          <div className="flex items-start justify-between gap-3">
            <p className="font-display text-2xl font-extrabold leading-snug tracking-tight text-[#12142b] sm:text-3xl">{latest.body}</p>
            <Controls n={latest} />
          </div>
          <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-[#565b73]">
            {latest.author_name || latest.author_email}{latest.author_role ? ` · ${roleLabel(latest.author_role)}` : ""} · {timeAgo(latest.created_at)}
            {latest.pinned && <PinnedChip />}
          </p>
          <div className="mt-1">
            <ReplyToggle n={latest} />
            <Thread n={latest} />
          </div>
        </div>
      ) : (
        <p className="rounded-2xl bg-[#f4f6fb] py-8 text-center text-sm text-[#565b73]" data-testid="board-empty">The board is empty — post the first note.</p>
      )}

      {/* Composer */}
      <div className="mt-4 flex items-start gap-3">
        <div className="flex-1">
          <textarea
            data-testid="board-input" rows={2} maxLength={280} value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") post(); }}
            placeholder="Write a note for the team…"
            className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-4 py-3 text-sm text-[#12142b] outline-none focus:border-[#2340b5]"
          />
          <p className="mt-1 text-right text-xs text-[#565b73]" data-testid="board-char-count">{text.length} / 280</p>
        </div>
        <button data-testid="board-post" onClick={post} disabled={posting || !text.trim()}
          className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50">
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
        </button>
      </div>

      {/* Past notes */}
      {rest.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-[#12142b]/8 pt-4" data-testid="board-history">
          {rest.map((n) => (
            <div key={n.id} data-testid={`board-note-row-${n.id}`} className="flex items-start gap-3 rounded-xl bg-[#f4f6fb] p-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">{initials(n.author_name || n.author_email)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#12142b]">{n.body}</p>
                <p className="mt-0.5 flex items-center gap-2 text-xs text-[#565b73]">
                  {n.author_name || n.author_email}{n.author_role ? ` · ${roleLabel(n.author_role)}` : ""} · {timeAgo(n.created_at)}
                  {n.pinned && <PinnedChip />}
                </p>
                <ReplyToggle n={n} light />
                <Thread n={n} />
              </div>
              <Controls n={n} />
            </div>
          ))}
        </div>
      )}

      {/* Personal notes from admin (staff view) */}
      {!isSuper && (
        <div className="mt-5 border-t border-[#12142b]/8 pt-4" data-testid="staff-notes">
          <p className="mb-3 text-sm font-bold text-[#12142b]">Notes from admin</p>
          {myNotes.length === 0 ? (
            <p className="text-xs text-[#565b73]" data-testid="staff-notes-empty">No notes yet — messages from the admin will appear here.</p>
          ) : (
            <div className="space-y-2">
              {myNotes.slice(0, 5).map((n) => {
                const [label, color, bg] = {
                  normal: ["Normal", "#2340b5", "#2340b512"],
                  important: ["Important", "#b45309", "#f59e0b1f"],
                  urgent: ["Urgent", "#e4002b", "#e4002b14"],
                }[n.priority] || ["Normal", "#2340b5", "#2340b512"];
                return (
                  <div key={n.id} data-testid={`staff-note-row-${n.id}`} className="flex items-start justify-between gap-3 rounded-xl bg-[#f4f6fb] p-3">
                    <div className="min-w-0">
                      <p className="text-sm text-[#12142b]">{n.body}</p>
                      <p className="mt-0.5 text-xs text-[#565b73]">{timeAgo(n.created_at)}</p>
                    </div>
                    <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color, backgroundColor: bg }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    "in_progress": ["In Progress", "#2340b5", "#2340b510"],
    "scheduled": ["Scheduled", "#f59e0b", "#f59e0b14"],
  };
  const [t, c, bg] = map[status] || [status, "#565b73", "#565b7314"];
  return (
    <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: c, backgroundColor: bg }}>
      {t}
    </span>
  );
};

export default function Dashboard() {
  const { admin } = useAdminAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => adminApi.get("/stats").then((r) => setData(r.data)).catch(() => setErr("Failed to load stats")), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (err) return <div className="p-10 text-[#e4002b]">{err}</div>;
  if (!data) return <div className="grid min-h-screen place-items-center text-[#565b73]">Loading dashboard…</div>;

  const supportCards = [
    { label: "Open Tickets", value: data.support.open_tickets, icon: Ticket, color: "#2340b5" },
    { label: "Disputes", value: data.support.disputes, icon: Gavel, color: "#f59e0b" },
    { label: "Chat", value: data.support.chats, icon: MessageCircle, color: "#16a34a" },
    { label: "Resolved Today", value: data.support.resolved_today, icon: CheckCheck, color: "#7c3aed" },
  ];

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#12142b]/8 bg-white/85 px-8 py-4 backdrop-blur">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[#12142b]">Dashboard</h1>
          <p className="text-sm text-[#565b73]">Overview of SafeRide operations and platform performance</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-2 rounded-lg border border-[#12142b]/10 px-3 py-2 text-sm text-[#565b73] md:flex">
            <Calendar className="h-4 w-4" /> Last 7 days
          </span>
          <button onClick={load} className="flex items-center gap-2 rounded-lg border border-[#12142b]/10 px-3 py-2 text-sm font-medium text-[#16a34a] transition-colors hover:bg-[#f4f6fb]" data-testid="refresh-stats">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <div className="flex items-center gap-2 rounded-lg bg-[#f4f6fb] px-3 py-1.5">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-[#2340b5] font-display text-sm font-bold text-white">
              {admin?.name?.[0] || "A"}
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold leading-none text-[#12142b]">{admin?.name}</p>
              <p className="text-xs text-[#565b73]">{admin?.role === "super_admin" ? "Super Admin" : admin?.role}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="space-y-6 p-8">
        {/* Shared team board */}
        <BoardPanel />

        {/* Alert row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Link to="/admin/m/fraud-alerts" data-testid="alert-fraud" className="flex items-center justify-between rounded-2xl border border-[#e4002b]/20 bg-[#e4002b]/5 p-5 transition-colors hover:bg-[#e4002b]/10">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#e4002b]/12"><AlertTriangle className="h-5 w-5 text-[#e4002b]" strokeWidth={1.75} /></span>
              <div><p className="font-display text-base font-bold text-[#12142b]">{data.alerts.fraud_alerts} Fraud Alerts</p><p className="text-xs text-[#565b73]">Requires attention</p></div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#e4002b]" />
          </Link>
          <Link to="/admin/users?verification=pending" data-testid="alert-verifications" className="flex items-center justify-between rounded-2xl border border-[#16a34a]/20 bg-[#16a34a]/5 p-5 transition-colors hover:bg-[#16a34a]/10">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#16a34a]/12"><ShieldCheck className="h-5 w-5 text-[#16a34a]" strokeWidth={1.75} /></span>
              <div><p className="font-display text-base font-bold text-[#12142b]">{data.alerts.pending_verifications} Pending User Verifications</p><p className="text-xs text-[#565b73]">Review required</p></div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#16a34a]" />
          </Link>
          <Link to="/admin/m/safety-center" data-testid="alert-sos" className="flex items-center justify-between rounded-2xl border border-[#7c3aed]/20 bg-[#7c3aed]/5 p-5 transition-colors hover:bg-[#7c3aed]/10">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#7c3aed]/12"><Siren className="h-5 w-5 text-[#7c3aed]" strokeWidth={1.75} /></span>
              <div><p className="font-display text-base font-bold text-[#12142b]">{data.alerts.emergency_sos} Emergency SOS Reports</p><p className="text-xs text-[#565b73]">View reports</p></div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#7c3aed]" />
          </Link>
        </div>

        {/* Live trips + communities */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-[#12142b]">Live Trips</h2>
              <Link to="/admin/m/ride-management" className="text-sm font-semibold text-[#2340b5]">View All</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
                    <th className="pb-3 font-semibold">Driver</th>
                    <th className="pb-3 font-semibold">Rider</th>
                    <th className="pb-3 font-semibold">Community</th>
                    <th className="pb-3 font-semibold">ETA</th>
                    <th className="pb-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.live_trips.map((t) => (
                    <tr key={t.id} className="border-b border-[#12142b]/5 last:border-0">
                      <td className="py-3 font-medium text-[#12142b]">{t.driver}</td>
                      <td className="py-3 text-[#565b73]">{t.rider}</td>
                      <td className="py-3 text-[#565b73]">{t.community}</td>
                      <td className="py-3 text-[#565b73]">{t.eta_min} min</td>
                      <td className="py-3"><StatusPill status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-display text-lg font-bold text-[#12142b]">Active Communities</h2>
            <div className="space-y-3">
              {data.communities.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl bg-[#f4f6fb] p-3">
                  <div>
                    <p className="text-sm font-semibold text-[#12142b]">{c.name}</p>
                    <p className="text-xs text-[#565b73]">{c.members} members</p>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold text-[#2340b5]">
                    <UsersRound className="h-3.5 w-3.5" /> {c.rides_today} rides
                  </span>
                </div>
              ))}
              {data.communities.length === 0 && (
                <p className="rounded-xl bg-[#f4f6fb] p-4 text-center text-sm text-[#565b73]">No active communities</p>
              )}
            </div>
          </div>
        </div>

        {/* Support overview */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-[#12142b]">Support Overview</h2>
            <Link to="/admin/support" className="text-sm font-semibold text-[#2340b5]">View all support tickets</Link>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {supportCards.map((s) => (
              <div key={s.label} className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm">
                <span className="grid h-10 w-10 place-items-center rounded-xl" style={{ backgroundColor: `${s.color}14` }}>
                  <s.icon className="h-5 w-5" strokeWidth={1.75} style={{ color: s.color }} />
                </span>
                <p className="mt-3 font-display text-2xl font-black text-[#12142b]">{s.value}</p>
                <p className="text-sm text-[#565b73]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
